import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import Footer from '../components/shared/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Calculator, ArrowRight, ArrowLeft, Check, X, AlertCircle,
  FileText, Truck, Globe, Loader2, Info, Download
} from 'lucide-react';

const countries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco', 
  'Tanzania', 'Ethiopia', 'Rwanda', 'Senegal', "Cote d'Ivoire", 
  'Cameroon', 'Uganda', 'Zimbabwe', 'Botswana', 'Namibia', 'Tunisia', 
  'Algeria', 'Angola', 'Benin'
];

const hsCodeExamples = [
  { code: '1801.00', description: 'Cocoa beans, whole or broken, raw or roasted' },
  { code: '0901.11', description: 'Coffee, not roasted, not decaffeinated' },
  { code: '5208.11', description: 'Woven cotton fabrics, unbleached' },
  { code: '3004.90', description: 'Medicaments, packaged for retail sale' },
  { code: '2523.29', description: 'Portland cement' },
];

const TariffCalculatorPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    export_country: '',
    import_country: '',
    hs_code: '',
    product_value: '',
    product_composition: '',
    value_added_percentage: '',
  });

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.export_country && formData.import_country;
      case 2:
        return formData.hs_code && formData.product_value;
      case 3:
        return formData.value_added_percentage;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateStep()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const calculateTariff = async () => {
    if (!validateStep()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tariffs/calculate`, {
        export_country: formData.export_country,
        import_country: formData.import_country,
        hs_code: formData.hs_code,
        product_value: parseFloat(formData.product_value),
        product_composition: formData.product_composition,
        value_added_percentage: parseFloat(formData.value_added_percentage),
      });
      
      setResult(response.data);
      setStep(4);
    } catch (error) {
      toast.error('Failed to calculate tariff. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetCalculator = () => {
    setFormData({
      export_country: '',
      import_country: '',
      hs_code: '',
      product_value: '',
      product_composition: '',
      value_added_percentage: '',
    });
    setResult(null);
    setStep(1);
  };

  const progress = (step / 4) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className={isAuthenticated ? 'flex' : ''}>
        {/* Sidebar for authenticated users */}
        {isAuthenticated && <DashboardSidebar />}

        <div className={isAuthenticated ? 'flex-1' : ''}>
          {/* Hero */}
          <section className={`bg-charcoal text-white py-12 ${isAuthenticated ? 'mx-6 mt-6 rounded-lg' : ''}`} data-testid="tariff-hero">
            <div className="container-main">
              <div className="max-w-2xl">
                <Badge className="bg-tradeGold text-black mb-4">AfCFTA Compliance</Badge>
                <h1 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">
                  Tariff & Rules of Origin Calculator
                </h1>
                <p className="text-gray-300">
                  Determine if your products qualify for preferential tariff treatment under AfCFTA. 
                  Get instant eligibility results and required documentation.
                </p>
              </div>
            </div>
          </section>

          <div className={isAuthenticated ? 'p-6' : 'container-main py-12'}>
            <div className="max-w-3xl mx-auto">
              {/* Progress */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Step {step} of 4</span>
                  <span className="text-sm font-medium text-continental">{Math.round(progress)}% Complete</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              {/* Step 1: Trade Route */}
              {step === 1 && (
                <Card data-testid="step-1">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-continental" />
                      Trade Route
                    </CardTitle>
                    <CardDescription>Select the export and import countries for your trade route</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Export Country (Origin)</Label>
                  <Select 
                    value={formData.export_country} 
                    onValueChange={(v) => handleChange('export_country', v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="export-country-select">
                      <SelectValue placeholder="Select export country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Import Country (Destination)</Label>
                  <Select 
                    value={formData.import_country} 
                    onValueChange={(v) => handleChange('import_country', v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="import-country-select">
                      <SelectValue placeholder="Select import country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.filter(c => c !== formData.export_country).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-oceanBlue flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-600">
                      <p className="font-medium text-oceanBlue mb-1">AfCFTA Coverage</p>
                      <p>All 54 African Union member states are part of AfCFTA. Preferential tariffs apply when both countries have ratified the agreement.</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={nextStep} className="btn-primary" data-testid="next-btn">
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Product Details */}
          {step === 2 && (
            <Card data-testid="step-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-continental" />
                  Product Details
                </CardTitle>
                <CardDescription>Enter the HS code and value of your product</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>HS Code</Label>
                  <Input
                    value={formData.hs_code}
                    onChange={(e) => handleChange('hs_code', e.target.value)}
                    placeholder="e.g., 1801.00"
                    className="mt-1"
                    data-testid="hs-code-input"
                  />
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Common examples:</p>
                    <div className="flex flex-wrap gap-2">
                      {hsCodeExamples.map((ex) => (
                        <button
                          key={ex.code}
                          onClick={() => handleChange('hs_code', ex.code)}
                          className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                        >
                          {ex.code}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Product Value (USD)</Label>
                  <Input
                    type="number"
                    value={formData.product_value}
                    onChange={(e) => handleChange('product_value', e.target.value)}
                    placeholder="Enter total shipment value"
                    className="mt-1"
                    data-testid="product-value-input"
                  />
                </div>

                <div>
                  <Label>Product Description (Optional)</Label>
                  <Input
                    value={formData.product_composition}
                    onChange={(e) => handleChange('product_composition', e.target.value)}
                    placeholder="Brief description of the product"
                    className="mt-1"
                  />
                </div>

                <div className="flex justify-between">
                  <Button onClick={prevStep} variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={nextStep} className="btn-primary">
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Rules of Origin */}
          {step === 3 && (
            <Card data-testid="step-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-continental" />
                  Rules of Origin
                </CardTitle>
                <CardDescription>Specify the local value addition for your product</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Local Value Added (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.value_added_percentage}
                    onChange={(e) => handleChange('value_added_percentage', e.target.value)}
                    placeholder="Percentage of value added locally"
                    className="mt-1"
                    data-testid="value-added-input"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    AfCFTA generally requires a minimum of 40% local value addition for preferential treatment.
                  </p>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-600">
                      <p className="font-medium text-amber-700 mb-1">Rules of Origin Criteria</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Products must be wholly obtained or substantially transformed</li>
                        <li>Minimum local content threshold applies (typically 40%)</li>
                        <li>Some products have specific rules based on HS code</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button onClick={prevStep} variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={calculateTariff} 
                    className="btn-primary"
                    disabled={loading}
                    data-testid="calculate-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Calculating...
                      </>
                    ) : (
                      <>
                        Calculate Tariff
                        <Calculator className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Results */}
          {step === 4 && result && (
            <div className="space-y-6" data-testid="step-4">
              {/* Result Summary */}
              <Card className={result.eligible ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                      result.eligible ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      {result.eligible ? (
                        <Check className="w-8 h-8 text-green-600" />
                      ) : (
                        <AlertCircle className="w-8 h-8 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-charcoal font-poppins">
                        {result.eligible ? 'AfCFTA Eligible' : 'Not Eligible'}
                      </h2>
                      <p className="text-gray-600">{formData.export_country} → {formData.import_country}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Original Tariff</p>
                      <p className="text-2xl font-bold text-charcoal">{result.original_tariff}%</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">AfCFTA Tariff</p>
                      <p className="text-2xl font-bold text-continental">{result.tariff_rate.toFixed(1)}%</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Tariff Reduction</p>
                      <p className="text-2xl font-bold text-tradeGold">{result.tariff_reduction}%</p>
                    </div>
                  </div>

                  <p className="text-gray-700">{result.explanation}</p>
                </CardContent>
              </Card>

              {/* Rules of Origin */}
              <Card>
                <CardHeader>
                  <CardTitle>Rules of Origin Assessment</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{result.rules_of_origin}</p>
                </CardContent>
              </Card>

              {/* Required Documents */}
              <Card>
                <CardHeader>
                  <CardTitle>Required Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {result.required_documents.map((doc) => (
                      <li key={doc} className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-continental/10 rounded-full flex items-center justify-center">
                          <FileText className="w-3 h-3 text-continental" />
                        </div>
                        <span className="text-gray-700">{doc}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-6 pt-6 border-t flex flex-wrap gap-3">
                    <Button onClick={() => navigate('/documents')} className="btn-primary">
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Documents
                    </Button>
                    <Button variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Download Report
                    </Button>
                    <Button variant="outline" onClick={resetCalculator}>
                      New Calculation
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {!isAuthenticated && <Footer />}
    </div>
  );
};

export default TariffCalculatorPage;
